import {
  createContext,
  Suspense,
  use,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {ActionList, ActionMenu, Button, IconButton, Stack, Text} from '@primer/react'
import {Filter} from '@github-ui/filter'
import {announce} from '@github-ui/aria-live'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {clsx} from 'clsx'
import {FilterIcon} from '@primer/octicons-react'
import {useDockedPanelMetadata, useIsDockedPanelCompact} from '../../../contexts/DockedPanelContext'
import {useInfiniteTimelinePageData} from '../../../page-data/loaders/use-timeline'
import {usePullRequestBasePath} from '../../../stores/selectors'
import {TimelineItemList} from './TimelineItemList'
import {TimelineSortMenu} from './TimelineSortMenu'
import {useInfiniteScrollLoad} from './use-infinite-scroll-load'
import {
  initialTimelineQuery,
  activityTypes,
  knownSinceValues,
  parseTimelineQuery,
  shouldAutoLoadTimelineScope,
  timelineQualifierValues,
  timelineFilterProviders,
  updateTimelineQualifier,
} from './timeline-filters'
import styles from './TimelinePanel.module.css'

// Restoring the original text after an edit must not restore the summary's pagination bound.
const TimelineQueryContext = createContext<string | null>(null)
const TimelineQuerySetterContext = createContext<(query: string) => void>(() => {})
const relativeSinceOptions = [
  {label: 'Past 24 hours', days: 1},
  {label: 'Past 7 days', days: 7},
  {label: 'Past 30 days', days: 30},
]

export function TimelineFilterProvider({children}: {children: ReactNode}) {
  const metadata = useDockedPanelMetadata()
  const [previousMetadata, setPreviousMetadata] = useState(metadata)
  const initialQuery = initialTimelineQuery(metadata)
  const [editedQuery, setEditedQuery] = useState<string | null>(null)
  const setQuery = useCallback(
    (value: string) => setEditedQuery(previous => (previous === null && value === initialQuery ? null : value)),
    [initialQuery],
  )
  // Opening another summary row replaces metadata even when the panel is already visible.
  if (metadata !== previousMetadata) {
    setPreviousMetadata(metadata)
    setEditedQuery(null)
  }
  return (
    <TimelineQuerySetterContext value={setQuery}>
      <TimelineQueryContext value={editedQuery}>{children}</TimelineQueryContext>
    </TimelineQuerySetterContext>
  )
}

function useTimelineQuery() {
  const editedQuery = use(TimelineQueryContext)
  const metadata = useDockedPanelMetadata()
  const query = editedQuery ?? initialTimelineQuery(metadata)
  return useMemo(() => parseTimelineQuery(query, metadata), [query, metadata])
}

function useTimelineAnnouncement(message: string) {
  const elementRef = useRef<HTMLDivElement>(null)
  const previousMessageRef = useRef(message)
  useEffect(() => {
    const changed = previousMessageRef.current !== message
    previousMessageRef.current = message
    if (!changed || !message) return
    // Waits for the message to settle. A newer message cancels this one without silencing later ones.
    const timeout = setTimeout(() => announce(message, {element: elementRef.current ?? undefined}), 300)
    return () => clearTimeout(timeout)
  }, [message])
  return elementRef
}

function ActorOptions({selectedActors, onChange}: {selectedActors: string[]; onChange: (actors: string[]) => void}) {
  const pathName = usePullRequestBasePath()
  const {data} = useInfiniteTimelinePageData({pathName})
  const actors = useMemo(() => {
    const users = new Map<string, {login: string; id: string}>()
    for (const page of data.pages) {
      for (const item of page.data) {
        if (item.user?.displayLogin) {
          users.set(item.user.displayLogin, {login: item.user.displayLogin, id: String(item.user.id)})
        }
      }
    }
    return [...users.values()].sort((a, b) => a.login.localeCompare(b.login))
  }, [data.pages])

  if (actors.length === 0) return <ActionList.Item disabled>No actors in loaded activity</ActionList.Item>

  return actors.map(actor => {
    const isActorValue = (value: string) => value.toLowerCase() === actor.login.toLowerCase() || value === actor.id
    const selected = selectedActors.some(isActorValue)
    return (
      <ActionList.Item
        key={actor.login}
        selected={selected}
        onSelect={() =>
          onChange(selected ? selectedActors.filter(value => !isActorValue(value)) : [...selectedActors, actor.login])
        }
      >
        {actor.login}
      </ActionList.Item>
    )
  })
}

export function TimelineFilter() {
  const isCompact = useIsDockedPanelCompact()
  const editedQuery = use(TimelineQueryContext)
  const setQuery = use(TimelineQuerySetterContext)
  const metadata = useDockedPanelMetadata()
  const query = editedQuery ?? initialTimelineQuery(metadata)
  const providers = useMemo(() => timelineFilterProviders(metadata), [metadata])
  const {type: selectedTypes, actor: selectedActors, since} = timelineQualifierValues(query, metadata)
  const selectedSince = since[0]
  const sinceOptions = [...knownSinceValues(metadata).keys()]
  const activeCount = Number(selectedTypes.length > 0) + Number(selectedActors.length > 0) + Number(!!selectedSince)
  const update = (key: 'type' | 'actor' | 'since', values: string[]) =>
    setQuery(updateTimelineQualifier(query, metadata, key, values))
  const toggleType = (selected: string[], value: string) =>
    update(
      'type',
      selected.some(item => item.toLowerCase() === value.toLowerCase())
        ? selected.filter(item => item.toLowerCase() !== value.toLowerCase())
        : [...selected, value],
    )
  const {errors} = useTimelineQuery()
  const id = useId()
  const errorRef = useTimelineAnnouncement(errors.join(' '))
  return (
    <div className={clsx(styles.filterHeader, isCompact && 'mt-2')}>
      <div className={styles.filterControls}>
        <Filter
          id={id}
          className={styles.filterInput}
          label="Filter activity"
          visuallyHideLabel
          variant="input"
          placeholder="Filter activity"
          filterValue={query}
          onChange={setQuery}
          providers={providers}
          showValidationMessage={false}
          aria-describedby={`${id}-help ${id}-error`}
        />
        <ActionMenu>
          <ActionMenu.Anchor>
            <IconButton
              icon={FilterIcon}
              aria-label={`Activity filters${activeCount ? ` (${activeCount} active)` : ''}`}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay width="medium">
            <ActionList>
              <ActionList.Group selectionVariant="multiple">
                <ActionList.GroupHeading>Activity type</ActionList.GroupHeading>
                {activityTypes.map(type => (
                  <ActionList.Item
                    key={type.value}
                    selected={selectedTypes.some(value => value.toLowerCase() === type.value)}
                    onSelect={() => toggleType(selectedTypes, type.value)}
                  >
                    {type.displayName}
                  </ActionList.Item>
                ))}
              </ActionList.Group>
              <ActionList.Divider />
              <ActionList.Group selectionVariant="multiple">
                <ActionList.GroupHeading>Actor</ActionList.GroupHeading>
                {/* Only the menu reads activity, so opening the panel can't suspend the page. */}
                <ErrorBoundary
                  boundaryName="TimelineFilterActors"
                  fallback={<ActionList.Item disabled>Actors unavailable</ActionList.Item>}
                >
                  <Suspense fallback={<ActionList.Item disabled>Loading actors</ActionList.Item>}>
                    <ActorOptions selectedActors={selectedActors} onChange={values => update('actor', values)} />
                  </Suspense>
                </ErrorBoundary>
              </ActionList.Group>
              <ActionList.Divider />
              <ActionList.Group selectionVariant="single">
                <ActionList.GroupHeading>Since</ActionList.GroupHeading>
                <ActionList.Item selected={!selectedSince} onSelect={() => update('since', [])}>
                  Any time
                </ActionList.Item>
                {sinceOptions.map(option => (
                  <ActionList.Item
                    key={option}
                    selected={selectedSince === option}
                    onSelect={() => update('since', [option])}
                  >
                    {option}
                  </ActionList.Item>
                ))}
                {relativeSinceOptions.map(option => (
                  <ActionList.Item
                    key={option.label}
                    onSelect={() =>
                      update('since', [new Date(Date.now() - option.days * 24 * 60 * 60 * 1000).toISOString()])
                    }
                  >
                    {option.label}
                  </ActionList.Item>
                ))}
                {selectedSince && !sinceOptions.includes(selectedSince) && (
                  <ActionList.Item selected disabled>
                    Since {selectedSince}
                  </ActionList.Item>
                )}
              </ActionList.Group>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
        <TimelineSortMenu />
      </div>
      <p id={`${id}-help`} className="sr-only">
        Search loaded activity by username, commit summary, or commit SHA. All words must match; use quotes for a
        phrase. Combine text with type:, actor: (login or ID), and since: (available preset or ISO timestamp). Use
        commas for multiple types or actors.
      </p>
      <div ref={errorRef} id={`${id}-error`}>
        {errors.length > 0 && <p className={styles.filterHelp}>{errors.join(' ')}</p>}
      </div>
    </div>
  )
}

export function FilteredTimeline() {
  const pathName = usePullRequestBasePath()
  const metadata = useDockedPanelMetadata()
  const editedQuery = use(TimelineQueryContext)
  const {data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError} = useInfiniteTimelinePageData({
    pathName,
  })
  const {matches, errors} = useTimelineQuery()
  const isQueryValid = errors.length === 0
  const canRetry = isQueryValid && !isFetchingNextPage
  const items = useMemo(() => data.pages.flatMap(page => page.data), [data.pages])
  const filteredItems = useMemo(() => items.filter(matches), [items, matches])
  const hasMoreScopedItems = shouldAutoLoadTimelineScope({
    activityGroup: metadata?.activityGroup,
    isQueryEdited: editedQuery !== null,
    isQueryValid,
    hasNextPage,
    isFetchNextPageError,
    matchingCount: filteredItems.length,
  })
  const hasMore =
    metadata?.activityGroup && editedQuery === null
      ? hasMoreScopedItems
      : isQueryValid && hasNextPage && !isFetchNextPageError
  const sentinel = useInfiniteScrollLoad({
    fetchNextPage,
    hasMore,
    isFetchingNextPage,
    announceLoading: false,
  })
  const summary = `Showing ${filteredItems.length} of ${items.length} loaded events`
  const announcementRef = useTimelineAnnouncement(
    isFetchingNextPage
      ? 'Loading more activity'
      : isFetchNextPageError
        ? 'Unable to load more activity. Try again.'
        : isQueryValid
          ? summary
          : '',
  )
  const retryRef = useCallback(
    (button: HTMLButtonElement | null) => {
      if (!button) return
      return () => {
        if (document.activeElement === button) announcementRef.current?.focus({preventScroll: true})
      }
    },
    [announcementRef],
  )

  return (
    <div ref={announcementRef} role="region" aria-label="Timeline events" tabIndex={-1}>
      <Stack gap="condensed">
        <Text size="small">{isQueryValid ? summary : 'Fix the filter query to show activity.'}</Text>
      </Stack>
      {isQueryValid &&
        (filteredItems.length === 0 && items.length > 0 && !sentinel ? (
          <p className={styles.emptyState}>
            {isFetchNextPageError
              ? 'No loaded activity matches the query. Change the query or retry loading activity.'
              : 'No loaded activity matches the query. Change or clear the query.'}
          </p>
        ) : (
          <TimelineItemList
            items={filteredItems}
            clipSidebar={hasMore && isFetchingNextPage ? false : 'end'}
            sentinel={sentinel}
          />
        ))}
      {hasNextPage && isFetchNextPageError && (
        <Stack gap="condensed">
          <Text>Unable to load more activity. Try again.</Text>
          <Button
            ref={retryRef}
            aria-disabled={!canRetry}
            onClick={() => {
              if (canRetry) void fetchNextPage()
            }}
          >
            {isFetchingNextPage ? 'Loading more activity' : 'Retry loading activity'}
          </Button>
        </Stack>
      )}
    </div>
  )
}
